/**
 * Utility functions for handling API errors and extracting translatable error messages.
 */

interface ErrorResponse {
  error?: string;
  message?: string;
  status?: number;
  timestamp?: number;
  path?: string;
}

/**
 * Extracts the error reason from an API error response and returns a translated message.
 * 
 * @param error - The error object from catch block (e: any)
 * @param t - Translation function from useTranslation hook
 * @returns Translated error message with context (e.g., username) if available
 * 
 * @example
 * try {
 *   await adminService.createAdmin(adminData);
 * } catch (e: any) {
 *   setError(getErrorTranslation(e, t));
 * }
 */
export const getErrorTranslation = (error: any, t: (key: string) => string): string => {
  const errorResponse: ErrorResponse | undefined = error?.response?.data;
  
  if (!errorResponse) {
    return error?.message || t('errors.UnknownError');
  }
  
  const errorType = errorResponse.error;
  
  if (!errorType) {
    return errorResponse.message || t('errors.UnknownError');
  }
  
  switch (errorType) {
    case 'DuplicateUsername':
      return t('errors.DuplicateUsername');
    case 'DuplicateEmail':
      return t('errors.DuplicateEmail');
    case 'DuplicateResource':
      return t('errors.DuplicateResource');
    case 'ValidationError':
      return t('errors.ValidationError');
    case 'ResourceNotFound':
      return t('errors.ResourceNotFound');
    case 'AccessDenied':
      return t('errors.AccessDenied');
    case 'Unauthorized':
      return t('errors.Unauthorized');
    case 'InvalidUserNameOrPassword':
      return t('errors.InvalidUserNameOrPassword');
    default:
      return t('errors.UnknownError');
  }
};

